// Kas & Bank Classification — Phase 1 (2026-02).
// Helper client: API + label + arah mutasi. Sumber tunggal utk 3 tab
// (Penerimaan/Pembayaran/Transfer Bank) + sub-tab "Belum Diklasifikasi".
import api from "@/lib/api";

export const KIND_META = {
  penerimaan: { label: "Penerimaan", classes: "bg-emerald-50 text-emerald-700 border-emerald-200", direction: "in" },
  pembayaran: { label: "Pembayaran", classes: "bg-rose-50 text-rose-700 border-rose-200", direction: "out" },
  transfer:   { label: "Transfer Bank", classes: "bg-sky-50 text-sky-700 border-sky-200", direction: "any" },
};

export const KIND_ORDER = ["penerimaan", "pembayaran", "transfer"];

export const kindLabel = (k) => KIND_META[k]?.label || "—";

export function isInbound(movementId) {
  if (!movementId) return false;
  return movementId.startsWith("receipt:") || movementId.startsWith("income-legacy:") || movementId.startsWith("pc-return:") || movementId.startsWith("estatement-split:in:") || movementId.startsWith("estatement:in:");
}

/** Jenis yang diperbolehkan utk 1 movement (search hanya kind valid arah). */
export function allowedKinds(movementId) {
  if (!movementId) return [];
  return isInbound(movementId)
    ? ["penerimaan", "transfer"]
    : ["pembayaran", "transfer"];
}

export async function fetchSummary() {
  const { data } = await api.get("/kas-bank/classifications/summary");
  return data || { penerimaan: 0, pembayaran: 0, transfer: 0, unclassified: 0, total: 0, unavailable: 0 };
}

export async function fetchMovements(kind) {
  const q = kind ? `?kind=${encodeURIComponent(kind)}` : "";
  const { data } = await api.get(`/kas-bank/movements${q}`);
  return Array.isArray(data) ? data : [];
}

export async function upsertClassification(payload) {
  const { data } = await api.post("/kas-bank/classifications", payload);
  return data;
}

export async function deleteClassification(movementId) {
  const { data } = await api.delete(`/kas-bank/classifications/${encodeURIComponent(movementId)}`);
  return data;
}

export async function fetchCOA() {
  // Sumber dropdown kategori (aktif saja).
  // 2026-09-29 (user request): akun INDUK (is_parent — kodenya prefix akun
  // lain, mis. 1102 → 110201) tidak boleh dipilih pada transaksi.
  const { data } = await api.get("/finance/chart-of-accounts");
  return Array.isArray(data) ? data.filter((c) => c.is_active !== false && !c.is_parent) : [];
}

export async function bulkClassify(payload) {
  const { data } = await api.post("/kas-bank/classifications/bulk", payload);
  return data;
}

// 2026-02 (user request): saran COA otomatis berdasarkan deskripsi mutasi bank.
// Backend membandingkan deskripsi vs `keywords` tiap COA (dipelihara admin di
// Master Data Keuangan). Kembalikan { [desc]: [{coa_id, code, name, score, keyword}] }.
export async function suggestCOA(descriptions) {
  const list = (Array.isArray(descriptions) ? descriptions : [descriptions])
    .filter((d) => typeof d === "string" && d.trim());
  if (list.length === 0) return {};
  try {
    const { data } = await api.post("/finance/chart-of-accounts/suggest", { descriptions: list });
    return data?.suggestions || {};
  } catch {
    return {};
  }
}

/** 2026-02-13 (user request): label proyek yg membedakan parent vs turunan.
 *  Proyek turunan (VO/WO) mewarisi `project_name` parent → tampilkan nama VO/WO-nya
 *  di depan, dgn nama parent sebagai konteks di belakang:
 *    main → "Earthwork Retail Promenade"
 *    VO   → "VO · Asiana Lido - Pagar Dinding Sisi Jalan Lapangan 1 (Pekerjaan Pembangunan…)"
 *    WO   → "WO · WOTAX-9a230d (TEST_WOTAX_PAR_02c3d8)"
 */
export function projectDisplayLabel(p, byId = null) {
  if (!p) return "—";
  const type = p.project_type;
  const parent = p.parent_project_id && byId ? byId.get(p.parent_project_id) : null;
  const parentName = parent ? (parent.project_name || parent.name) : null;
  if (type === "variation_order") {
    const nm = p.variation_order_name || p.wo_number || p.project_name || p.name || p.id;
    return parentName ? `VO · ${nm} (${parentName})` : `VO · ${nm}`;
  }
  if (type === "working_order") {
    const nm = p.wo_number || p.variation_order_name || p.project_name || p.name || p.id;
    return parentName ? `WO · ${nm} (${parentName})` : `WO · ${nm}`;
  }
  return p.project_name || p.name || p.id;
}

/** Opsi SearchableSelect utk dropdown proyek dgn label pembeda VO/WO. */
export function buildProjectOptions(projects, emptyLabel = "— Tanpa Proyek —") {
  const byId = new Map((projects || []).map((p) => [p.id, p]));
  return [
    { value: "", label: emptyLabel },
    ...(projects || []).map((p) => ({ value: p.id, label: projectDisplayLabel(p, byId) })),
  ];
}

/** Bangun struktur `groups` untuk SearchableSelect dgn 1 blok "Disarankan" di atas.
 *  - `suggested` array dari objek {coa_id, code, name, keyword}
 *  - `allCoa` daftar COA lengkap (aktif)
 *  Opsi kosong ("— Tanpa Kategori —") tetap tersedia di grup utama.
 */
export function buildCoaGroupsWithSuggestions(suggested, allCoa, emptyLabel = "— Tanpa Kategori —") {
  const suggestedIds = new Set((suggested || []).map((s) => s.coa_id));
  const groups = [];
  if ((suggested || []).length > 0) {
    groups.push({
      label: "🔮 Disarankan (dari kata kunci COA)",
      // 2026-02 (user request): tanda "≈" utk saran fuzzy (deskripsi mirip, ada typo).
      options: suggested.map((s) => ({ value: s.coa_id, label: `${s.code} · ${s.name}  ⟵  ${s.match === "fuzzy" ? "≈ " : ""}"${s.keyword}"` })),
    });
  }
  groups.push({
    label: null,
    options: [
      { value: "", label: emptyLabel },
      ...(allCoa || []).filter((c) => !suggestedIds.has(c.id)).map((c) => ({ value: c.id, label: `${c.code} · ${c.name}` })),
    ],
  });
  return groups;
}

export function exportMovementsUrl(kind) {
  const q = kind ? `?kind=${encodeURIComponent(kind)}` : "";
  return `/kas-bank/movements/export.xlsx${q}`;
}

export async function downloadExport(kind) {
  const q = kind ? `?kind=${encodeURIComponent(kind)}` : "";
  const res = await api.get(`/kas-bank/movements/export.xlsx${q}`, { responseType: "blob" });
  const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `kas_bank_${(kind || "semua").toLowerCase()}.xlsx`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
